import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDT, todayIST } from "@/lib/api";
import { PageHeader, Empty, Field, StatusBadge, tableWrap, th, td, num } from "@/components/common";

function NewCount({ onClose }) {
  const { lookups } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ date: todayIST(), brand: "", remarks: "" });
  const save = async () => {
    try { const r = await api.post("/counts", f); toast.success(`${r.data.count_no} created`); nav(`/reconciliation/${r.data.id}`); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="new-count-dialog">
        <DialogHeader><DialogTitle>Start a physical count</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <Field label="Count date" required><Input type="date" max={todayIST()} value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="count-date-input" /></Field>
          <Field label="Brand">
            <select className="h-10 rounded-md border bg-background px-3 text-sm" value={f.brand} onChange={(e) => setF({ ...f, brand: e.target.value })} data-testid="count-brand-select">
              <option value="">All brands</option>
              {(lookups?.brands || []).map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </Field>
          <Field label="Remarks"><Input value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} data-testid="count-remarks-input" /></Field>
          <p className="text-xs text-muted-foreground">The sheet lists every brand and cable size with its current system stock. Staff enter the total counted for each one.</p>
        </div>
        <DialogFooter><Button onClick={save} data-testid="count-create-btn">Create count sheet</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Counts() {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get("/counts").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  return (
    <div>
      <PageHeader title="Stock Reconciliation" subtitle="Physical stock counts by brand and cable size. Variances are posted only after Super Admin approval.">
        {can("count:create") && <Button onClick={() => setOpen(true)} className="bg-red-600 hover:bg-red-700 text-white" data-testid="new-count-btn"><Plus className="h-4 w-4 mr-1" />New Physical Count</Button>}
      </PageHeader>
      <div className={tableWrap}>
        <table className="w-full" data-testid="counts-table">
          <thead><tr>{["Count No", "Date", "Scope", "Status", "Counted", "With Variance", "Created By", "Created"].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.id} className="hover:bg-muted/40" data-testid={`count-row-${r.count_no}`}>
              <td className={`${td} font-mono`}><Link className="hover:underline text-primary" to={`/reconciliation/${r.id}`}>{r.count_no}</Link></td>
              <td className={td}>{r.date}</td><td className={td}>{r.brand}</td><td className={td}><StatusBadge status={r.status} /></td>
              <td className={`${td} ${num}`}>{r.lines_counted}</td><td className={`${td} ${num}`}>{r.lines_with_variance}</td>
              <td className={td}>{r.created_by?.name}</td><td className={td}>{fmtDT(r.created_at)}</td>
            </tr>
          ))}</tbody>
        </table>
        {!rows.length && <Empty text="No physical counts yet" />}
      </div>
      {open && <NewCount onClose={() => setOpen(false)} />}
    </div>
  );
}
